import type { CaseFormat } from '../../components/shared/CaseFormatSelector';

export interface MergeTokenDefinition {
  name: string;
  label: string;
  group: string;
  dataPath?: string;
  sampleValue?: string;
}

/**
 * List of standard dynamic merge tokens grouped by domain category
 */
export const MERGE_TOKENS_LIST: MergeTokenDefinition[] = [
  // Applicant Data
  { name: 'first_name', label: 'First Name', group: 'Applicant Data', dataPath: 'personalDetails.firstName', sampleValue: 'John' },
  { name: 'last_name', label: 'Last Name', group: 'Applicant Data', dataPath: 'personalDetails.lastName', sampleValue: 'Doe' },
  { name: 'full_name', label: 'Full Name', group: 'Applicant Data', sampleValue: 'John Doe' },
  { name: 'middle_name', label: 'Middle Name', group: 'Applicant Data', dataPath: 'personalDetails.middleName', sampleValue: 'Michael' },
  { name: 'email', label: 'Email Address', group: 'Applicant Data', dataPath: 'userEmail', sampleValue: 'john.doe@example.com' },
  { name: 'phone', label: 'Phone Number', group: 'Applicant Data', dataPath: 'personalDetails.phone', sampleValue: '+1 555-0199' },
  { name: 'mobile_number', label: 'Mobile Number', group: 'Applicant Data', dataPath: 'personalDetails.mobileNumber', sampleValue: '+1 555-0199' },
  { name: 'date_of_birth', label: 'Date of Birth', group: 'Applicant Data', dataPath: 'personalDetails.dob', sampleValue: '1998-05-15' },
  { name: 'gender', label: 'Gender', group: 'Applicant Data', dataPath: 'personalDetails.gender', sampleValue: 'Male' },
  { name: 'nationality', label: 'Nationality', group: 'Applicant Data', dataPath: 'personalDetails.countryOfCitizenship', sampleValue: 'French' },
  { name: 'country_of_birth', label: 'Country of Birth', group: 'Applicant Data', dataPath: 'personalDetails.countryOfBirth', sampleValue: 'France' },
  { name: 'city_of_birth', label: 'City of Birth', group: 'Applicant Data', dataPath: 'personalDetails.cityOfBirth', sampleValue: 'Paris' },
  { name: 'marital_status', label: 'Marital Status', group: 'Applicant Data', dataPath: 'personalDetails.maritalStatus', sampleValue: 'Single' },

  // Program & Placement Info
  { name: 'application_id', label: 'Application ID', group: 'Program Info', dataPath: 'applicationId', sampleValue: 'APP-10204' },
  { name: 'application_status', label: 'Application Status', group: 'Program Info', dataPath: 'status', sampleValue: 'Approved' },
  { name: 'program_name', label: 'Program Name', group: 'Program Info', sampleValue: 'J-1 Internship Program' },
  { name: 'program_type', label: 'Program Type', group: 'Program Info', dataPath: 'programType', sampleValue: 'Internship' },
  { name: 'program_term', label: 'Program Term', group: 'Program Info', sampleValue: 'Spring 2025' },
  { name: 'batch_name', label: 'Batch Name', group: 'Program Info', dataPath: 'batchName', sampleValue: '2025-Cohort-A' },
  { name: 'placement_employer', label: 'Host Employer', group: 'Employer Info', sampleValue: 'Marriott Resort' },
  { name: 'placement_location', label: 'Placement Location', group: 'Employer Info', sampleValue: 'Miami, FL' },
  { name: 'placement_start_date', label: 'Start Date', group: 'Employer Info', sampleValue: '2025-06-01' },
  { name: 'placement_end_date', label: 'End Date', group: 'Employer Info', sampleValue: '2026-05-31' },

  // Passport & Visa
  { name: 'passport_number', label: 'Passport Number', group: 'Passport & Visa', sampleValue: 'AB1234567' },
  { name: 'passport_country', label: 'Passport Country', group: 'Passport & Visa', sampleValue: 'France' },
  { name: 'visa_number', label: 'Visa Number', group: 'Passport & Visa', sampleValue: 'V9876543' },
  { name: 'visa_type', label: 'Visa Type', group: 'Passport & Visa', sampleValue: 'J-1' },
  { name: 'ds160_number', label: 'DS-160 Number', group: 'Passport & Visa', sampleValue: 'AA00123456' },

  // Portal URLs & System
  { name: 'portal_link', label: 'Portal Link', group: 'Portal & System', sampleValue: 'https://portal.isats.org/applicant/APP-10204' },
  { name: 'today', label: 'Today Date', group: 'Portal & System', sampleValue: 'May 1, 2025' },
  { name: 'current_date', label: 'Current Date & Time', group: 'Portal & System', sampleValue: '5/1/2025, 10:30:00 AM' },
];

/**
 * Apply case transformation to a raw string value
 */
export function formatCaseValue(value: string, format: CaseFormat | string): string {
  if (!value || !format || format === 'original') return value;
  const fmt = String(format).toLowerCase();

  switch (fmt) {
    case 'upper':
    case 'uppercase':
      return value.toUpperCase();
    case 'lower':
    case 'lowercase':
      return value.toLowerCase();
    case 'capitalize':
      return value.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    case 'title':
    case 'titlecase':
      return value.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    case 'sentence':
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    case 'toggle':
      return value.replace(/./g, (c) => (c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase()));
    default:
      return value;
  }
}

/**
 * Parse a merge field tag (e.g. "applicant.first_name | uppercase" or "first_name:upper")
 * into normalized fieldName and case format.
 */
export function parseMergeTag(tag: string): { fieldName: string; format: CaseFormat } {
  // Strip outer {{ and }}
  let inner = tag.trim();
  if (inner.startsWith('{{')) inner = inner.slice(2);
  if (inner.endsWith('}}')) inner = inner.slice(0, -2);
  inner = inner.trim();

  // Match separator | or :
  const match = inner.match(/^(.*?)\s*[:|]\s*([a-zA-Z0-9_-]+)$/);
  if (match) {
    let rawPath = match[1].trim();
    const rawFormat = match[2].trim().toLowerCase();

    let format: CaseFormat = 'original';
    if (['uppercase', 'upper'].includes(rawFormat)) format = 'upper';
    else if (['lowercase', 'lower'].includes(rawFormat)) format = 'lower';
    else if (['capitalize'].includes(rawFormat)) format = 'capitalize';
    else if (['titlecase', 'title'].includes(rawFormat)) format = 'title';
    else if (['sentence'].includes(rawFormat)) format = 'sentence';
    else if (['toggle'].includes(rawFormat)) format = 'toggle';

    // Normalize field name prefix (e.g. applicant.first_name -> first_name)
    if (rawPath.startsWith('applicant.')) rawPath = rawPath.slice(10);
    if (rawPath.startsWith('applicantData.')) rawPath = rawPath.slice(14);

    return { fieldName: rawPath, format };
  }

  let fieldName = inner;
  if (fieldName.startsWith('applicant.')) fieldName = fieldName.slice(10);
  if (fieldName.startsWith('applicantData.')) fieldName = fieldName.slice(14);

  return { fieldName, format: 'original' };
}

export interface ResolveMergeOptions {
  /** Base URL for {{portal_link}} — the server passes its FRONTEND_URL;
   *  the browser falls back to window.__FRONTEND_URL / the portal origin. */
  baseUrl?: string;
}

/**
 * Best-effort full name for an applicant record: structured
 * firstName/lastName first, then a combined fullName/name string (the
 * shapes the server's resolveApplicantName also handles for plain,
 * already-decrypted data).
 */
export function applicantFullName(a: any): string {
  const pd = a?.personalDetails || {};
  const parts = [pd.firstName, pd.lastName].filter(
    (v: any) => typeof v === 'string' && v.trim(),
  );
  if (parts.length) return parts.join(' ');
  for (const cand of [pd.fullName, pd.name, a?.fullName, a?.name]) {
    if (typeof cand === 'string' && cand.trim()) return cand.trim();
  }
  return '';
}

/** The next upcoming event on an application record (shared by the
 *  next_event_* tokens — mirrors the historical server-side resolver). */
function nextUpcomingEvent(a: any): any | null {
  const evts = Array.isArray(a?.events) ? a.events : [];
  const upcoming = evts
    .filter((e: any) => e?.date && new Date(e.date).getTime() > Date.now())
    .sort((x: any, y: any) => new Date(x.date).getTime() - new Date(y.date).getTime());
  return upcoming[0] || null;
}

/**
 * Resolve simple tags map for a given applicant record.
 *
 * This module is the ONE source of truth for merge-field resolution
 * (PR-11 §E): the browser previews, the send-now job handler AND the
 * Express server (send / schedule / preview endpoints, via delegation)
 * all resolve through here — the former duplicate map in server.ts,
 * which had drifted ahead on the personal-details/event tokens, is gone.
 */
export function resolveSimpleTagValue(
  app: any,
  fieldName: string,
  fallback = 'N/A',
  options?: ResolveMergeOptions,
): string {
  if (!app) return fallback;

  const simpleTagMap: Record<string, (a: any) => string> = {
    first_name: (a) => applicantFullName(a).split(' ')[0] || 'N/A',
    last_name: (a) => applicantFullName(a).split(' ').slice(1).join(' ') || 'N/A',
    full_name: (a) => applicantFullName(a) || a.userEmail || 'N/A',
    middle_name: (a) => a.personalDetails?.middleName || 'N/A',
    email: (a) => a.userEmail || 'N/A',
    phone: (a) => a.personalDetails?.phone || a.phone || 'N/A',
    mobile_number: (a) => a.personalDetails?.mobileNumber || 'N/A',
    date_of_birth: (a) => a.personalDetails?.dob || 'N/A',
    gender: (a) => a.personalDetails?.gender || 'N/A',
    nationality: (a) => a.personalDetails?.countryOfCitizenship || 'N/A',
    country_of_birth: (a) => a.personalDetails?.countryOfBirth || 'N/A',
    city_of_birth: (a) => a.personalDetails?.cityOfBirth || 'N/A',
    marital_status: (a) => a.personalDetails?.maritalStatus || 'N/A',
    legal_permanent_residence: (a) => a.personalDetails?.legalPermanentResidenceCountry || 'N/A',
    full_time_student: (a) => (a.personalDetails?.fullTimeStudent === true ? 'Yes' : 'No'),
    objective: (a) => a.personalDetails?.objective || 'N/A',
    primary_language: (a) => a.personalDetails?.primaryLanguage || 'N/A',
    other_languages: (a) => a.personalDetails?.otherLanguages || 'N/A',
    computer_skills: (a) => a.personalDetails?.computerSkills || 'N/A',
    countries_resided: (a) => a.personalDetails?.countriesResided || 'N/A',
    current_country_of_residence: (a) => a.personalDetails?.currentCountryOfResidence || 'N/A',
    personal_skills: (a) => a.personalDetails?.personalSkills || 'N/A',
    leisure_activities: (a) => a.personalDetails?.leisureActivities || 'N/A',
    hospitality_experience: (a) => a.personalDetails?.hospitalityExperience || 'N/A',
    address: (a) => {
      const addr = a.personalDetails?.address;
      if (!addr) return 'N/A';
      return [addr.street, addr.city, addr.state, addr.zip, addr.country].filter(Boolean).join(', ') || 'N/A';
    },
    address_street: (a) => a.personalDetails?.address?.street || 'N/A',
    address_city: (a) => a.personalDetails?.address?.city || 'N/A',
    address_state: (a) => a.personalDetails?.address?.state || 'N/A',
    address_zip: (a) => a.personalDetails?.address?.zip || 'N/A',
    address_country: (a) => a.personalDetails?.address?.country || 'N/A',
    application_id: (a) => a.applicationId || 'N/A',
    application_status: (a) => a.status || a.submissionStatus || 'N/A',
    master_phase: (a) => {
      const s = a.masterStage ?? a.pipelineStage;
      return s != null ? String(s) : 'N/A';
    },
    active_status: (a) => (a.activeStatus !== false && a.activeStatus !== undefined ? 'Active' : 'Inactive'),
    program_name: (a) => a.assignment?.programName || a.programName || a.programType || 'N/A',
    program_type: (a) => a.programType || 'N/A',
    program_duration: (a) => a.programPreferences?.programDuration || 'N/A',
    program_term: (a) => a.assignment?.programTerm || a.programPreferences?.programTerm || 'N/A',
    program_variable_name: (a) => a.pricingVariableName || a.pricingVariableSnapshot?.pricingVariableName || 'N/A',
    pricing_variable_name: (a) => a.pricingVariableName || a.pricingVariableSnapshot?.pricingVariableName || 'N/A',
    program_variable: (a) => a.pricingVariableName || a.pricingVariableSnapshot?.pricingVariableName || 'N/A',
    variable_code: (a) => a.pricingVariableCode || a.pricingVariableSnapshot?.pricingVariableCode || 'N/A',
    pricing_variable_code: (a) => a.pricingVariableCode || a.pricingVariableSnapshot?.pricingVariableCode || 'N/A',
    applicable_selling_price: (a) => {
      const sp = a.pricingVariableSellingPrice ?? a.pricingVariableSnapshot?.pricingVariableSellingPrice;
      return sp != null ? `$${Number(sp).toFixed(2)}` : 'N/A';
    },
    pricing_variable_selling_price: (a) => {
      const sp = a.pricingVariableSellingPrice ?? a.pricingVariableSnapshot?.pricingVariableSellingPrice;
      return sp != null ? `$${Number(sp).toFixed(2)}` : 'N/A';
    },
    selling_price: (a) => {
      const sp = a.pricingVariableSellingPrice ?? a.pricingVariableSnapshot?.pricingVariableSellingPrice;
      return sp != null ? `$${Number(sp).toFixed(2)}` : 'N/A';
    },
    pricing_variable_currency: (a) => a.pricingVariableCurrency || a.pricingVariableSnapshot?.pricingVariableCurrency || 'USD',
    batch_name: (a) => a.batchName || 'N/A',
    application_created_date: (a) => (a.createdAt ? new Date(a.createdAt).toLocaleString() : 'N/A'),
    portal_link: (a) =>
      a.applicationId
        ? `${options?.baseUrl || (typeof window !== 'undefined' && window.__FRONTEND_URL ? window.__FRONTEND_URL : 'https://portal.isats.org')}/applicant/${a.applicationId}`
        : 'N/A',
    placement_status: (a) => a.placementStatus || 'Pending',
    placement_employer: (a) => a.assignment?.employerName || a.serviceProvider?.name || 'N/A',
    placement_location: (a) => [a.assignment?.city, a.assignment?.state].filter(Boolean).join(', ') || 'N/A',
    placement_start_date: (a) => a.assignment?.startDate || 'N/A',
    placement_end_date: (a) => a.assignment?.endDate || 'N/A',
    host_company: (a) => a.assignment?.employerName || 'N/A',
    host_address: (a) => [a.assignment?.address, a.assignment?.city, a.assignment?.state, a.assignment?.country].filter(Boolean).join(', ') || 'N/A',
    host_city: (a) => a.assignment?.city || 'N/A',
    host_state: (a) => a.assignment?.state || 'N/A',
    host_country: (a) => a.assignment?.country || 'N/A',
    passport_number: (a) => a.passportImmigration?.passportNumber || 'N/A',
    passport_country: (a) => a.passportImmigration?.passportCountry || 'N/A',
    passport_issue_date: (a) => a.passportImmigration?.issueDate || 'N/A',
    passport_expiry_date: (a) => a.passportImmigration?.expirationDate || 'N/A',
    visa_number: (a) => a.visaDetails?.visaNumber || a.passportImmigration?.visaNumber || 'N/A',
    visa_type: (a) => a.visaDetails?.visaType || a.passportImmigration?.visaType || 'N/A',
    visa_issue_date: (a) => a.visaDetails?.issueDate || 'N/A',
    visa_expiry_date: (a) => a.visaDetails?.expirationDate || 'N/A',
    visa_conditions: (a) => a.visaDetails?.conditions || a.visaDetails?.annotations || 'N/A',
    ds160_number: (a) => a.ds160Details?.ds160Number || a.visaDetails?.ds160ConfirmationNumber || 'N/A',
    ds160_status: (a) => a.ds160Details?.submissionStatus || 'N/A',
    embassy_appointment_datetime: (a) => a.embassyDetails?.appointmentDate || a.visaDetails?.embassyAppointmentDate || 'N/A',
    embassy_appointment_location: (a) => a.embassyDetails?.location || a.visaDetails?.embassyLocation || 'N/A',
    embassy_interview_result: (a) => a.embassyDetails?.interviewResult || 'N/A',
    emergency_name: (a) => {
      const c = a.emergencyContacts?.[0];
      return c?.fullName || c?.name || 'N/A';
    },
    emergency_phone: (a) => {
      const c = a.emergencyContacts?.[0];
      return c?.phone || 'N/A';
    },
    emergency_relationship: (a) => {
      const c = a.emergencyContacts?.[0];
      return c?.relationship || 'N/A';
    },
    next_event_name: (a) => nextUpcomingEvent(a)?.name || 'N/A',
    next_event_date: (a) => {
      const ev = nextUpcomingEvent(a);
      return ev?.date ? new Date(ev.date).toLocaleString() : 'N/A';
    },
    next_event_location: (a) => nextUpcomingEvent(a)?.location || nextUpcomingEvent(a)?.link || 'N/A',
    document_required: (a) => a.documentRequired || 'N/A',
    today: () => new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    current_date: () => new Date().toLocaleString(),
  };

  const fn = simpleTagMap[fieldName];
  if (fn) {
    const val = fn(app);
    return val !== undefined && val !== null ? String(val) : fallback;
  }

  // Fallback: dot-path lookup
  const parts = fieldName.split('.');
  let val: any = app;
  for (const p of parts) {
    if (val == null) return fallback;
    if (/^\d+$/.test(p) && Array.isArray(val)) {
      val = val[parseInt(p)];
      continue;
    }
    val = val[p];
  }
  return val != null && val !== undefined ? String(val) : fallback;
}

/**
 * Hydrate all {{placeholder}} tokens inside a template string against an applicant record
 */
export function resolveMergeFields(
  template: string,
  app: any,
  fallback = 'N/A',
  options?: ResolveMergeOptions,
): string {
  if (!template) return '';
  return template.replace(/\{\{([\s\S]+?)\}\}/g, (_match: string, tagContent: string) => {
    const { fieldName, format } = parseMergeTag(tagContent);
    const rawVal = resolveSimpleTagValue(app, fieldName, fallback, options);
    return formatCaseValue(rawVal, format);
  });
}
